import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  createOrder,
  emptyCart,
  getShoppingCartItems,
  updateOrder as updateCartOrder,
  updateCartProducts,
  deleteShoppingCart
} from '../../api/action/cart';
import { updateOrder as updateOrderStatus } from '../../api/action/order';
import { findEligibleDiscounts } from '../../api/action/discount';
import { setOrder } from '../../api/reducers/orderReducer';
import { DOMAIN_TYPES, getCartByDomain } from '../../domain/Domain';
import { DomainLoading } from '../../domain/Domain';
import { initiatePayment, setReturnURL } from '../../api/action/user';
import { useCart } from '../../context/CartContext';
import {
  buildZellePendingTransaction,
  getDefaultGatewayPaymentMethod,
  getManualPaymentDisplayDetails,
  getOfflinePaymentSavedMessage,
  getOfflineOrderStatus,
  getSelectedPaymentOption,
  getSelectablePaymentOptions,
  getZellePaymentMethod,
  isCashPaymentOption,
  normalizeOfflineReferenceId,
  requiresOfflineReference
} from './paymentMethodUtils';
import {
  calculateRetailCartTotal,
  normalizeRetailCartItems,
  updateRetailCartQuantity
} from '../../domains/commerce/services/retailCartPricing';

const EMPTY_COMPANY = {};
const EMPTY_PAYMENT_METHODS = [];
const RETAIL_DISCOUNT_DURATION_TYPES = ["HOUR", "DAY", "WEEK", "MONTH", "YEAR"];

const getCartItemProductId = (item) => item?.product?.primaryId || item?.scheduledProduct?.product?.primaryId;

const getDiscountProductIds = (discount) => new Set([
  ...(discount?.criteria?.productAttributes || []),
  ...(discount?.associatedProducts || [])
].map((product) => product?.primaryId).filter((id) => id != null));

const isDiscountEligibleForCartItem = (discount, item) => {
  const discountProductIds = getDiscountProductIds(discount);
  return discountProductIds.size === 0 || discountProductIds.has(getCartItemProductId(item));
};

const calculateDiscountAmount = (discount, subtotal, items = []) => {
  const safeSubtotal = Number(subtotal || 0);
  if (!discount || safeSubtotal <= 0) return 0;

  const applyType = String(discount.applyType || '').toUpperCase();
  const eligibleItems = items.filter((item) => isDiscountEligibleForCartItem(discount, item));
  const eligibleSubtotal = eligibleItems.reduce((total, item) => {
    const itemTotal = Number(item?.totalPrice);
    if (Number.isFinite(itemTotal)) return total + itemTotal;

    const product = item?.product || item?.scheduledProduct?.product;
    return total + (Number(product?.price?.priceAmt || 0) * Number(item?.quantity || 1));
  }, 0);
  const rawDiscount = applyType === 'PERCENT'
    ? (eligibleSubtotal * Number(discount.percentage || 0)) / 100
    : Number(discount.amount || 0) * eligibleItems.length;

  if (!Number.isFinite(rawDiscount) || rawDiscount <= 0) return 0;
  return Math.min(safeSubtotal, Math.round(rawDiscount * 100) / 100);
};

const getCheckoutErrorMessage = (error, fallback = "Checkout failed. Please try again.") => {
  const message = error?.message || error?.response?.data?.message || error?.data?.message || "";
  if (String(message).includes("Duplicate purchase is not allowed")) {
    return "This course has already been purchased. You can purchase it again after the current enrollment expires.";
  }
  return message || fallback;
};

const Cart = () => {
  const {
    clearCart: clearCartContext,
    updateCart: updateCartContext
  } = useCart();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  const userDetail = useSelector((state) => state?.auth?.data) || {};
  const selectedCompanyState = useSelector((state) => state.companyReducer.company);
  const companyState = selectedCompanyState || EMPTY_COMPANY;
  const selectedBranch = useSelector((state) => state.selectedBranch);
  const domainType = localStorage.getItem("domainType") || DOMAIN_TYPES.RETAIL;
  const currencySymbol = companyState?.operatingLocation?.code === "US" ? "$" : "₹";

  const isRetailCart = domainType === DOMAIN_TYPES.RETAIL;
  const [cartData, setCartData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [, setOrderDetails] = useState(null);
  const [loadingItems, setLoadingItems] = useState({});
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("razorPay");
  const [pendingOfflineOrder, setPendingOfflineOrder] = useState(null);
  const [showCashConfirmDialog, setShowCashConfirmDialog] = useState(false);
  const [showZelleModal, setShowZelleModal] = useState(false);
  const [isSubmittingOfflinePayment, setIsSubmittingOfflinePayment] = useState(false);
  const [zelleReferenceId, setZelleReferenceId] = useState("");
  const [alertMessage, setAlertMessage] = useState({
    open: false,
    message: '',
    severity: 'error'
  });
  const companyPaymentMethods = companyState?.account?.paymentMethods || EMPTY_PAYMENT_METHODS;
  const paymentOptions = React.useMemo(
    () => getSelectablePaymentOptions(
      companyState?.operatingLocation?.code,
      companyPaymentMethods,
      companyState
    ),
    [companyPaymentMethods, companyState]
  );
  const selectedPaymentOption = getSelectedPaymentOption(paymentOptions, paymentMethod);
  const manualPaymentOption = requiresOfflineReference(selectedPaymentOption)
    ? selectedPaymentOption
    : paymentOptions.find((option) => option.requiresReference) || null;
  const zellePaymentMethod = getZellePaymentMethod(companyPaymentMethods);
  const manualPaymentDetails = getManualPaymentDisplayDetails(
    companyState,
    manualPaymentOption,
    pendingOfflineOrder?.number
  );
  const normalizedZelleReferenceId = normalizeOfflineReferenceId(zelleReferenceId);
  const discountAmount = React.useMemo(
    () => calculateDiscountAmount(appliedDiscount, totalAmount, cartData),
    [appliedDiscount, cartData, totalAmount]
  );
  const checkoutTotal = Math.max(0, Math.round((totalAmount - discountAmount) * 100) / 100);
  const bulkProduct = cartData.length === 1
    ? (cartData[0]?.product || cartData[0]?.scheduledProduct?.product)
    : null;
  const bulkOrderEligible = Boolean(
    isRetailCart
    && cartData.length === 1
    && !cartData[0]?.scheduledProduct
    && companyState?.bulkOrderEnabled
    && bulkProduct?.bulkOrderEnabled
  );

  const clearRetailDiscount = React.useCallback(() => {
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountError("");
  }, []);

  const handleApplyDiscount = async () => {
    const code = discountCode.trim().toUpperCase();
    if (!code) {
      setDiscountError("Please enter a discount code");
      return;
    }

    const productIds = Array.from(new Set(cartData
      .map((item) => item.product?.primaryId || item.scheduledProduct?.product?.primaryId)
      .filter((id) => id != null)));
    const companyId = selectedBranch?.primaryId || companyState?.primaryId;

    if (productIds.length === 0 || !companyId) {
      setDiscountError("Discount eligibility could not be checked for this cart");
      return;
    }

    setIsApplyingDiscount(true);
    setDiscountError("");

    try {
      const productAttributes = productIds.map((primaryId) => ({ primaryId }));
      const response = await dispatch(findEligibleDiscounts({
        associatedProducts: productAttributes,
        company: { primaryId: companyId },
        criteria: {
          productAttributes,
          durationTypes: RETAIL_DISCOUNT_DURATION_TYPES
        },
        code,
        status: "ACTIVE"
      }));
      const discounts = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.payload)
            ? response.payload
            : response?.primaryId
              ? [response]
              : [];
      const eligibleDiscount = discounts.find((discount) => discount?.eligible !== false);

      if (!eligibleDiscount) {
        setAppliedDiscount(null);
        setDiscountError("This discount code is not valid for the items in your cart");
        return;
      }

      const amount = calculateDiscountAmount(eligibleDiscount, totalAmount, cartData);
      if (amount <= 0) {
        setAppliedDiscount(null);
        setDiscountError("This discount does not reduce the current cart total");
        return;
      }

      setDiscountCode(eligibleDiscount.code || code);
      setAppliedDiscount(eligibleDiscount);
      setAlertMessage({
        open: true,
        message: "Discount applied successfully!",
        severity: "success"
      });
    } catch (error) {
      setAppliedDiscount(null);
      setDiscountError(error?.message || "Failed to apply discount. Please try again.");
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  useEffect(() => {
    const defaultGatewayMethod = getDefaultGatewayPaymentMethod(
      companyState?.operatingLocation?.code,
      companyPaymentMethods
    );
    const nextPaymentMethod = paymentOptions.find((option) => option.value === defaultGatewayMethod)?.value
      || paymentOptions[0]?.value
      || "";

    setPaymentMethod((currentMethod) => (
      paymentOptions.some((option) => option.value === currentMethod)
        ? currentMethod
        : nextPaymentMethod
    ));
  }, [companyPaymentMethods, companyState?.operatingLocation?.code, paymentOptions]);

  const loadCart = React.useCallback(async () => {
    try {
      const shoppId = userDetail?.account?.shoppingCart?.primaryId;
      if (!shoppId) {
        setCartData([]);
        clearCartContext();
        setIsLoading(false);
        return;
      }

      const data = await getShoppingCartItems(shoppId);
      if (data) {
        setIsLoading(false);
        const rawProductList = data[0].productList || [];
        const prodList = isRetailCart
          ? normalizeRetailCartItems(rawProductList)
          : rawProductList;
        localStorage.setItem("cartProducts", JSON.stringify(prodList));
        const loadedTotal = isRetailCart
          ? calculateRetailCartTotal(prodList)
          : Number(data[0].totalAmount);
        setTotalAmount(Number.isFinite(loadedTotal) ? loadedTotal : 0);
        setCartData(prodList);
        updateCartContext(prodList);
      }
    } catch {
      setAlertMessage({
        open: true,
        message: "Failed to load cart items",
        severity: "error"
      });
      setCartData([]);
      setIsLoading(false);
    }
  }, [
    clearCartContext,
    isRetailCart,
    updateCartContext,
    userDetail?.account?.shoppingCart?.primaryId
  ]);

  useEffect(() => {
    loadCart();
  }, [isAuthenticated, loadCart]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1 || newQuantity > 10) return;

    // Set loading state for this specific item
    setLoadingItems(prev => ({ ...prev, [itemId]: true }));

    try {
      const updatedItems = isRetailCart
        ? updateRetailCartQuantity(cartData, itemId, newQuantity)
        : cartData.map(item => {
          if (item.product?.primaryId === itemId) {
            const price = Number(item.product?.price?.priceAmt);
            return {
              ...item,
              quantity: newQuantity,
              totalPrice: (Number.isFinite(price) ? price : 0) * newQuantity
            };
          }
          if (item.scheduledProduct?.primaryId === itemId) {
            const price = Number(item.scheduledProduct?.product?.price?.priceAmt);
            return {
              ...item,
              quantity: newQuantity,
              totalPrice: (Number.isFinite(price) ? price : 0) * newQuantity
            };
          }
          return item;
        });

      const updatedTotal = isRetailCart
        ? calculateRetailCartTotal(updatedItems)
        : updatedItems.reduce((sum, item) => {
          const itemTotal = Number(item.totalPrice);
          return sum + (Number.isFinite(itemTotal) ? itemTotal : 0);
        }, 0);

      const shoppId = userDetail?.account?.shoppingCart?.primaryId;
      const updatedCart = {
        modifiedBy: userDetail?.firstName || "user",
        accountId: userDetail?.account?.primaryId,
        primaryId: shoppId,
        productList: updatedItems,
        totalPrice: updatedTotal,
        company: companyState
      };

      await updateCartProducts(updatedCart);

      // Update local state without reloading
      setCartData(updatedItems);
      setTotalAmount(updatedTotal);
      updateCartContext(updatedItems);
      clearRetailDiscount();

    } catch {
      setAlertMessage({
        open: true,
        message: "Failed to update item quantity",
        severity: "error"
      });
    } finally {
      // Clear loading state
      setLoadingItems(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleEmptyCart = async () => {
    try {
      const shoppId = userDetail?.account?.shoppingCart?.primaryId;

      if (!shoppId) {
        throw new Error("No shopping cart ID found");
      }

      const response = await emptyCart({
        primaryId: shoppId,
        accountId: userDetail?.account?.primaryId,
        createdBy: `${userDetail.firstName} ${userDetail.lastName}`,
        modifiedBy: `${userDetail.firstName} ${userDetail.lastName}`,
        productList: [],
        company: userDetail?.account?.company || {}
      });

      if (response?.productList?.length === 0) {
        localStorage.setItem('cartProducts', JSON.stringify([]));
        setCartData([]);
        setTotalAmount(0);
        clearCartContext();
        clearRetailDiscount();
        setAlertMessage({
          open: true,
          message: "Cart cleared successfully!",
          severity: "success"
        });
      } else {
        throw new Error("Cart is not empty after clearing");
      }
    } catch {
      setAlertMessage({
        open: true,
        message: "Failed to clear cart. Please try again.",
        severity: "error"
      });
    }
  };

  const handleDeleteCart = async () => {
    try {
      const shoppId = userDetail?.account?.shoppingCart?.primaryId;
      if (!shoppId) {
        setAlertMessage({
          open: true,
          message: "No shopping cart found",
          severity: "warning"
        });
        return;
      }

      const response = await deleteShoppingCart(shoppId);

      if (response?.productList?.length === 0) {
        localStorage.setItem('cartProducts', JSON.stringify([]));

        setCartData([]);
        setTotalAmount(0);
        clearCartContext();
        clearRetailDiscount();
        setAlertMessage({
          open: true,
          message: "Cart cleared successfully!",
          severity: "success"
        });
      } else {
        throw new Error("Failed to clear cart");
      }
    } catch {
      setAlertMessage({
        open: true,
        message: "Failed to clear cart. Please try again.",
        severity: "error"
      });
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      // Find item to get product name for message
      const cartItem = cartData.find(item =>
        item.product?.primaryId === itemId ||
        item.scheduledProduct?.primaryId === itemId
      );
      const productName = cartItem?.product?.name || cartItem?.scheduledProduct?.product?.name || 'item';

      // Show loading message
      setAlertMessage({
        open: true,
        message: `Removing ${productName} from cart...`,
        severity: 'info'
      });

      const shoppId = userDetail?.account?.shoppingCart?.primaryId;

      const filteredItems = cartData.filter(item =>
        item.product?.primaryId !== itemId && item.scheduledProduct?.primaryId !== itemId
      );

      const newTotalPrice = isRetailCart
        ? calculateRetailCartTotal(filteredItems)
        : filteredItems.reduce((sum, item) => {
          const itemTotal = Number(item.totalPrice);
          return sum + (Number.isFinite(itemTotal) ? itemTotal : 0);
        }, 0);

      const payload = {
        modifiedBy: userDetail?.firstName || "user",
        accountId: userDetail?.account?.primaryId,
        primaryId: shoppId,
        productList: filteredItems,
        totalPrice: newTotalPrice,
        company: companyState
      };

      await updateCartProducts(payload);

      const updatedCart = await getShoppingCartItems(shoppId);

      const refreshedItems = isRetailCart
        ? normalizeRetailCartItems(updatedCart[0].productList || [])
        : updatedCart[0].productList || [];
      const refreshedTotal = isRetailCart
        ? calculateRetailCartTotal(refreshedItems)
        : Number(updatedCart[0].totalAmount);

      setCartData(refreshedItems);
      setTotalAmount(Number.isFinite(refreshedTotal) ? refreshedTotal : 0);
      updateCartContext(refreshedItems);
      clearRetailDiscount();

      setAlertMessage({
        open: true,
        message: `${productName} removed from cart successfully!`,
        severity: "success"
      });

    } catch {
      setAlertMessage({
        open: true,
        message: "Failed to remove item from cart",
        severity: "error"
      });
    }
  };

  const addUpdateOrder = async (lineItemList) => {
    const orderRequest = {
      createdBy: `${userDetail.firstName} ${userDetail.lastName}`,
      modifiedBy: `${userDetail.firstName} ${userDetail.lastName}`,
      accountId: userDetail.account?.primaryId,
      totalLineItems: cartData.length,
      totalAmount: totalAmount,
      subtotalAmount: totalAmount,
      shippingAndHandlingAmount: 0,
      tax: 0,
      lineItemList,
      company: {
        primaryId: selectedBranch?.primaryId || companyState?.primaryId,
        domain: {
          type: companyState?.domain?.type,
        }
      }
    };

    const existingOrder = JSON.parse(localStorage.getItem("orderDetails"));
    let orderResponse;

    try {
      setIsProcessing(true);

      // Show processing message
      setAlertMessage({
        open: true,
        message: "Processing your order...",
        severity: 'info'
      });

      if (!existingOrder) {
        orderResponse = await createOrder(orderRequest);
      } else {
        orderResponse = await updateCartOrder({
          ...existingOrder,
          ...orderRequest
        });
      }

      localStorage.setItem("orderDetails", JSON.stringify(orderResponse));
      dispatch(setOrder(orderResponse));
      setOrderDetails(orderResponse);
      return orderResponse;
    } catch (error) {
      const message = getCheckoutErrorMessage(error, "Order processing failed. Please try again.");
      setAlertMessage({
        open: true,
        message,
        severity: "error"
      });
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayment = async (orderDetails) => {
    try {
      const paymentData = {
        countryCode: companyState?.operatingLocation?.code,
        orderId: orderDetails?.primaryId,
        accountId: userDetail?.account?.primaryId,
        orderNumber: orderDetails?.number,
        companyName: companyState.name,
        companyId: companyState.primaryId,
        amount: checkoutTotal,
        userId: userDetail.primaryId,
        transactionType: "PURCHASE",
        stripe: {
          amount: checkoutTotal,
          customerName: `${userDetail.firstName} ${userDetail.lastName}`,
          customerEmail: userDetail.email,
        },
        razorPay: {
          amount: checkoutTotal * 100,
          customer: {
            name: `${userDetail.firstName} ${userDetail.lastName}`,
            contact: userDetail.phoneNumber,
            email: userDetail.email,
          },
        },
      };

      const returnURL = window.location.host;
      const protocol = window.location.protocol;
      const countryCode = companyState?.operatingLocation?.code;

      await setReturnURL(protocol, returnURL, countryCode);

      const response = await initiatePayment(paymentData);

      if (response?.stripeResponse) {
        window.location.href = response.stripeResponse;
      } else if (response?.razorPayResponse) {
        window.location.href = response.razorPayResponse;
      } else {
        throw new Error("No payment URL received");
      }
    } catch (error) {
      setAlertMessage({
        open: true,
        message: error?.name === "PaymentError"
          ? error.message
          : "We couldn’t process your payment at this time. Please try again in a few moments.",
        severity: "error"
      });
    }
  };

  const handleFlexOfficeCheckout = async () => {
    try {
      const lineItemList = cartData.map(item => {
        if (item.product && !item.scheduledProduct) {
          return {
            quantity: item.quantity,
            totalPrice: item.totalPrice,
            product: {
              name: item.product?.name,
              code: item.product?.code,
              primaryId: item.product?.primaryId,
              price: {
                priceAmt: item.product?.price?.priceAmt,
              },
            }
          };
        }
        else {
          return {
            quantity: item.quantity,
            totalPrice: item.totalPrice,
            scheduledProduct: {
              product: {
                name: item.scheduledProduct?.product?.name,
                code: item.scheduledProduct?.product?.code,
                primaryId: item.scheduledProduct?.product?.primaryId,
                price: {
                  priceAmt: item.scheduledProduct?.product?.price?.priceAmt,
                },
              },
              schedule: {
                primaryId: item.scheduledProduct?.primaryId,
              },
              primaryId: item.scheduledProduct?.primaryId,
            }
          };
        }
      });

      setAlertMessage({
        open: true,
        message: "Creating your order...",
        severity: 'info'
      });

      const orderResponse = await addUpdateOrder(lineItemList);

      setAlertMessage({
        open: true,
        message: "Order created successfully! Processing payment...",
        severity: "success"
      });

      await new Promise(resolve => setTimeout(resolve, 2000));

      await handlePayment(orderResponse);

    } catch (error) {
      setAlertMessage({
        open: true,
        message: getCheckoutErrorMessage(error),
        severity: "error"
      });
    }
  };

  const clearCartAfterOfflineOrder = async () => {
    const shoppingCartId = userDetail?.account?.shoppingCart?.primaryId;
    if (!shoppingCartId) return;

    const emptyCartPayload = {
      primaryId: shoppingCartId,
      accountId: userDetail?.account?.primaryId,
      createdBy: `${userDetail?.firstName || ""} ${userDetail?.lastName || ""}`.trim() || "System",
      modifiedBy: `${userDetail?.firstName || ""} ${userDetail?.lastName || ""}`.trim() || "System",
      productList: [],
      company: userDetail?.account?.company || companyState || {}
    };

    try {
      await emptyCart(emptyCartPayload);
      localStorage.setItem("cartProducts", JSON.stringify([]));
      setCartData([]);
      setTotalAmount(0);
      clearCartContext();
      clearRetailDiscount();
    } catch {
    }
  };

  const finalizeOfflinePayment = async (selectedMethod) => {
    try {
      if (!pendingOfflineOrder?.primaryId) {
        throw new Error("Order details are missing. Please try again.");
      }

      if (!selectedMethod) {
        throw new Error("Selected payment method is not available.");
      }

      if (requiresOfflineReference(selectedMethod) && !normalizedZelleReferenceId) {
        throw new Error("Enter the Zelle reference ID before continuing.");
      }

      setIsSubmittingOfflinePayment(true);

      const modifiedBy = `${userDetail?.firstName || ""} ${userDetail?.lastName || ""}`.trim()
        || userDetail?.email
        || "System";
      const zelleTransaction = requiresOfflineReference(selectedMethod)
        ? buildZellePendingTransaction({
          amount: pendingOfflineOrder?.totalAmtAfterDiscount ?? pendingOfflineOrder?.dueAmount ?? pendingOfflineOrder?.totalAmount ?? checkoutTotal,
          orderPrimaryId: pendingOfflineOrder.primaryId,
          accountPrimaryId: userDetail?.account?.primaryId || pendingOfflineOrder?.accountId,
          userPrimaryId: userDetail?.primaryId,
          company: companyState,
          zellePaymentMethod,
          paymentMethodConfig: selectedMethod.paymentMethodConfig,
          referenceId: normalizedZelleReferenceId,
          createdBy: modifiedBy
        })
        : null;
      const nextStatus = getOfflineOrderStatus(selectedMethod.value);

      await dispatch(updateOrderStatus({
        primaryId: Number(pendingOfflineOrder.primaryId) || pendingOfflineOrder.primaryId,
        currentStatus: nextStatus,
        paymentMethod: selectedMethod.orderPaymentMethod,
        modifiedBy,
        ...(zelleTransaction ? { transactionList: [zelleTransaction] } : {})
      }, "order_status_update"));

      const updatedOrder = {
        ...pendingOfflineOrder,
        currentStatus: nextStatus,
        paymentMethod: selectedMethod.orderPaymentMethod,
        ...(zelleTransaction ? {
          transactionList: [
            ...(pendingOfflineOrder?.transactionList || []),
            zelleTransaction
          ]
        } : {})
      };

      localStorage.setItem("orderDetails", JSON.stringify(updatedOrder));
      localStorage.setItem("persistedOrder", JSON.stringify({
        ...updatedOrder,
        timestamp: new Date().getTime()
      }));
      localStorage.removeItem("orderDetails");
      dispatch(setOrder(updatedOrder));
      setOrderDetails(updatedOrder);

      await clearCartAfterOfflineOrder();

      setShowCashConfirmDialog(false);
      setShowZelleModal(false);
      setPendingOfflineOrder(null);
      setZelleReferenceId("");
      setAlertMessage({
        open: true,
        message: getOfflinePaymentSavedMessage(selectedMethod),
        severity: "success"
      });

      setTimeout(() => navigate("/customer-orders"), 1500);
    } catch (error) {
      setAlertMessage({
        open: true,
        message: error.message || "Failed to save offline payment.",
        severity: "error"
      });
    } finally {
      setIsSubmittingOfflinePayment(false);
    }
  };

  const handleRetailCheckout = async (selectedPaymentMethod = paymentMethod) => {
    setIsProcessing(true);
    try {
      setPendingOfflineOrder(null);
      setShowCashConfirmDialog(false);
      setShowZelleModal(false);

      setAlertMessage({
        open: true,
        message: "Processing your checkout...",
        severity: 'info'
      });

      const eligibleDiscounts = appliedDiscount ? [{
        primaryId: appliedDiscount.primaryId,
        applyType: appliedDiscount.applyType,
        percentage: appliedDiscount.percentage,
        amount: appliedDiscount.amount,
        code: appliedDiscount.code
      }] : [];
      const lineItemList = cartData.map(item => {
        if (item.product && !item.scheduledProduct) {
          return {
            quantity: item.quantity,
            totalPrice: item.totalPrice,
            eligibleDiscounts: isDiscountEligibleForCartItem(appliedDiscount, item) ? eligibleDiscounts : [],
            product: {
              name: item.product?.name,
              code: item.product?.code,
              primaryId: item.product?.primaryId,
              price: {
                priceAmt: item.product?.price?.priceAmt,
              },
            }
          };
        }
        else {
          return {
            quantity: item.quantity,
            totalPrice: item.totalPrice,
            eligibleDiscounts: isDiscountEligibleForCartItem(appliedDiscount, item) ? eligibleDiscounts : [],
            scheduledProduct: {
              product: {
                name: item.scheduledProduct?.product?.name,
                code: item.scheduledProduct?.product?.code,
                primaryId: item.scheduledProduct?.product?.primaryId,
                price: {
                  priceAmt: item.scheduledProduct?.product?.price?.priceAmt,
                },
              },
              schedule: {
                primaryId: item.scheduledProduct?.primaryId,
              },
              primaryId: item.scheduledProduct?.primaryId,
            }
          };
        }
      });

      const orderRequest = {
        createdBy: `${userDetail.firstName} ${userDetail.lastName}`,
        modifiedBy: `${userDetail.firstName} ${userDetail.lastName}`,
        accountId: userDetail.account?.primaryId,
        totalLineItems: cartData.length,
        totalAmount: totalAmount,
        subtotalAmount: totalAmount,
        totalDiscountAmt: discountAmount,
        totalAmtAfterDiscount: checkoutTotal,
        dueAmount: checkoutTotal,
        shippingAndHandlingAmount: 0,
        tax: 0,
        deliveryType: "BRICK_MORTAR",
        lineItemList,
        company: {
          primaryId: selectedBranch?.primaryId || companyState?.primaryId,
          bulkOrderEnabled: Boolean(companyState?.bulkOrderEnabled),
          domain: {
            type: companyState?.domain?.type,
          }
        },
        bulkOrderEnabled: bulkOrderEligible
      };

      const existingOrder = JSON.parse(localStorage.getItem("orderDetails"));
      let orderResponse;

      if (!existingOrder) {
        localStorage.removeItem('persistedOrder');
        orderResponse = await createOrder(orderRequest);
      } else {
        localStorage.removeItem('persistedOrder');
        orderResponse = await updateCartOrder({
          ...existingOrder,
          ...orderRequest
        });
      }

      localStorage.setItem("orderDetails", JSON.stringify(orderResponse));
      dispatch(setOrder(orderResponse));
      setOrderDetails(orderResponse);

      // The backend also evaluates the persisted company/product flags. Use
      // its result so older carts cannot fall back to the payment checkout.
      const savedBulkOrder = bulkOrderEligible || Boolean(orderResponse?.bulkOrderEnabled);
      if (savedBulkOrder) {
        await clearCartAfterOfflineOrder();
        setAlertMessage({
          open: true,
          message: 'Your bulk order booking has been received. No payment is required now. Once the minimum requirement is met and the admin approves the order, you will receive an invoice with a payment link.',
          severity: 'success'
        });
        return;
      }

      localStorage.setItem('persistedOrder', JSON.stringify({
        ...orderResponse,
        timestamp: new Date().getTime()
      }));

      const selectedMethod = getSelectedPaymentOption(paymentOptions, selectedPaymentMethod);

      if (isCashPaymentOption(selectedMethod)) {
        setPendingOfflineOrder(orderResponse);
        setShowCashConfirmDialog(true);
        setAlertMessage({
          open: true,
          message: "Order created. Confirm cash payment below.",
          severity: "success"
        });
        return;
      }

      if (requiresOfflineReference(selectedMethod)) {
        setPendingOfflineOrder(orderResponse);
        setShowZelleModal(true);
        setAlertMessage({
          open: true,
          message: `Order created. Complete the ${selectedMethod?.title || "payment"} steps below.`,
          severity: "success"
        });
        return;
      }

      localStorage.setItem('checkoutPaymentMethod', selectedPaymentMethod);

      setAlertMessage({
        open: true,
        message: "Checkout successful! Redirecting...",
        severity: "success"
      });

      await new Promise(resolve => setTimeout(resolve, 1500));

      navigate("/order-history");

    } catch (error) {
      setAlertMessage({
        open: true,
        message: getCheckoutErrorMessage(error),
        severity: "error"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCheckout = async (selectedPaymentMethod = paymentMethod) => {
    if (domainType === DOMAIN_TYPES.FLEXOFFICE) {
      await handleFlexOfficeCheckout();
    } else {
      await handleRetailCheckout(selectedPaymentMethod);
    }
  };

  const handleContinueShopping = () => {
    if (domainType === DOMAIN_TYPES.RETAIL) {
      navigate("/");
    } else if (domainType === DOMAIN_TYPES.EDUCATION) {
      navigate("/products");
    } else {
      navigate("/services");
    }
  };

  const handleCloseAlert = () => {
    setAlertMessage(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!alertMessage.open) return undefined;
    const timer = setTimeout(() => {
      setAlertMessage(prev => ({ ...prev, open: false }));
    }, 3000);
    return () => clearTimeout(timer);
  }, [alertMessage.open]);

  const AlertComponent = () => {
    return (
      alertMessage.open && (
        <div
          id="alert-message-container"
          className="fixed left-1/2 -translate-x-1/2"
          style={{
            top: 'calc(var(--layout-header-height, 80px) + 1rem)',
            zIndex: 100,
            width: 'calc(100% - 2rem)',
            maxWidth: '28rem'
          }}
          role="status"
          aria-live="polite"
        >
          <div
            className={`px-6 py-4 rounded-xl shadow-2xl backdrop-blur-sm ${alertMessage.severity === 'error'
              ? 'bg-red-600'
              : alertMessage.severity === 'success'
                ? 'bg-emerald-600'
                : alertMessage.severity === 'info'
                  ? 'bg-blue-600'
                  : 'bg-amber-500'
              } text-white`}
          >
            <div className="flex justify-between items-center">
              <span className="font-medium" id="alert-message-text">
                {alertMessage.message}
              </span>
              <button
                id="alert-close-button"
                onClick={handleCloseAlert}
                className="ml-4 text-xl hover:text-gray-200 transition-colors"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      )
    );
  };

  const renderDomainCart = () => {
    const commonProps = {
      items: cartData,
      totalAmount,
      currencySymbol,
      onRemoveItem: handleRemoveItem,
      onDeleteCart: handleDeleteCart,
      onQuantityChange: handleQuantityChange,
      onCheckout: handleCheckout,
      onCheckoutWithMethod: handleCheckout,
      selectedPaymentMethod: paymentMethod,
      onPaymentMethodChange: setPaymentMethod,
      paymentOptions,
      alertMessage,
      onCloseAlert: handleCloseAlert,
      onContinueShopping: handleContinueShopping,
      onEmptyCart: handleEmptyCart,
      userDetail,
      isLoading,
      isProcessing,
      discountCode,
      appliedDiscount,
      discountAmount,
      checkoutTotal,
      discountError,
      isApplyingDiscount,
      onDiscountCodeChange: (value) => {
        setDiscountCode(value);
        setDiscountError("");
      },
      onApplyDiscount: handleApplyDiscount,
      onRemoveDiscount: clearRetailDiscount,
      bulkOrderEnabled: bulkOrderEligible,
      loadCart,
      loadingItems // Pass loading state to child component
    };
    const CartComponent = getCartByDomain(domainType);
    return <CartComponent {...commonProps} />;
  };

  const ModalShell = ({ open, title, children, footer, onClose, maxWidthClass = "max-w-md" }) => {
    if (!open) return null;

    const modalTitle = title || "Dialog";
    const titleId = `${modalTitle.replace(/\s+/g, "-").toLowerCase()}-title`;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <button
          type="button"
          className="absolute inset-0 h-full w-full bg-black/40"
          onClick={onClose}
          aria-label={`Close ${modalTitle}`}
        />
        <div
          className={`relative w-full ${maxWidthClass} overflow-hidden rounded-2xl bg-white shadow-2xl`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div id={titleId} className="border-b border-gray-200 px-6 py-4 text-xl font-bold text-gray-900">
            {modalTitle}
          </div>
          <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
            {children}
          </div>
          {footer && (
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              {footer}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isProcessing) {
    return <DomainLoading />;
  }

  return (
    <>
      {renderDomainCart()}
      <ModalShell
        open={showCashConfirmDialog}
        onClose={() => !isSubmittingOfflinePayment && setShowCashConfirmDialog(false)}
        title="Confirm Cash Payment"
        maxWidthClass="max-w-sm"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setShowCashConfirmDialog(false)}
              disabled={isSubmittingOfflinePayment}
              className="rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => finalizeOfflinePayment(paymentOptions.find((option) => isCashPaymentOption(option)))}
              disabled={isSubmittingOfflinePayment}
              className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmittingOfflinePayment ? <Loader2 size={18} className="mr-2 animate-spin" /> : null}
              {isSubmittingOfflinePayment ? "Confirming..." : "Confirm"}
            </button>
          </>
        )}
      >
        <p className="text-sm leading-7 text-gray-600">
          Your order has been created. Confirm this cash payment to keep the order in payment pending status without leaving the cart page.
        </p>
        <div className="mt-4 rounded-xl bg-gray-50 p-4">
          <p className="text-xs text-gray-500">Order Number</p>
          <p className="text-base font-bold text-gray-900">
            {pendingOfflineOrder?.number || 'Pending'}
          </p>
        </div>
      </ModalShell>

      <ModalShell
        open={showZelleModal}
        onClose={() => !isSubmittingOfflinePayment && setShowZelleModal(false)}
        title={manualPaymentDetails.dialogTitle}
        maxWidthClass="max-w-lg"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setShowZelleModal(false)}
              disabled={isSubmittingOfflinePayment}
              className="rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => finalizeOfflinePayment(manualPaymentOption)}
              disabled={isSubmittingOfflinePayment || !normalizedZelleReferenceId}
              className="inline-flex items-center rounded-lg bg-violet-600 px-4 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmittingOfflinePayment ? <Loader2 size={18} className="mr-2 animate-spin" /> : null}
              {isSubmittingOfflinePayment ? "Saving..." : "Continue"}
            </button>
          </>
        )}
      >
        <div className="rounded-2xl bg-gradient-to-br from-violet-700 via-fuchsia-600 to-indigo-700 p-5 text-white">
          <p className="text-xs uppercase tracking-[0.2em] text-white/70">
            {manualPaymentDetails.title}
          </p>
          <p className="mt-1 text-xl font-bold">{manualPaymentDetails.recipientName}</p>
          <p className="mt-1 text-sm text-white/80">{manualPaymentDetails.referenceLabel}</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-white/15 p-3">
              <p className="text-xs text-white/70">Amount</p>
              <p className="text-base font-bold">
                {currencySymbol}{Number(pendingOfflineOrder?.totalAmtAfterDiscount ?? pendingOfflineOrder?.dueAmount ?? pendingOfflineOrder?.totalAmount ?? checkoutTotal ?? 0).toFixed(2)}
              </p>
            </div>
            <div className="rounded-xl bg-white/15 p-3">
              <p className="text-xs text-white/70">Order Ref</p>
              <p className="text-base font-bold">{pendingOfflineOrder?.number || 'Pending'}</p>
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-dashed border-violet-200 bg-violet-50 p-5 text-center">
          {manualPaymentDetails.qrImageUrl ? (
            <img
              src={manualPaymentDetails.qrImageUrl}
              alt={`${manualPaymentDetails.title} QR`}
              className="mx-auto h-44 w-44 rounded-2xl border border-violet-100 bg-white p-2 object-cover"
            />
          ) : (
            <div className="mx-auto flex h-44 w-44 items-center justify-center rounded-2xl border border-violet-100 bg-white p-4 text-center text-sm text-slate-500">
              Use the saved recipient details above to complete the payment.
            </div>
          )}
          <p className="mt-4 text-sm text-gray-700">
            Scan the QR code, send the full amount, then mark this order as payment processing.
          </p>
        </div>

        <label className="mt-5 block text-sm font-semibold text-gray-700">
          {manualPaymentDetails.title} Reference ID
          <input
            className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:bg-gray-100"
            placeholder={`Enter the ${manualPaymentDetails.title} confirmation ID`}
            value={zelleReferenceId}
            onChange={(event) => setZelleReferenceId(event.target.value)}
            disabled={isSubmittingOfflinePayment}
          />
        </label>
        <p className="mt-1 text-xs text-gray-500">{manualPaymentDetails.helperText}</p>
      </ModalShell>
      <AlertComponent />
    </>
  );
};

export default Cart;
